/**
 * React Query hooks for the at-risk student widget.
 *
 * Kept in its own file (rather than appended to hooks/index.ts) so the
 * widget's data layer is self-contained and easy to remove if the heuristic
 * is later moved server-side and surfaced via a different aggregate endpoint.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissStudent,
  getAtRiskStudents,
  type AtRiskFilters,
  type AtRiskStudent,
} from '../services/atRiskApi';
import { useToast } from '../components/Toast';

export const atRiskQueryKeys = {
  all: ['atRisk'] as const,
  list: (filters: AtRiskFilters) => ['atRisk', 'list', filters] as const,
};

/**
 * Poll the at-risk list every 60s. Risk signals are derived from activity
 * data that itself changes on the order of minutes, so a 1-minute cadence
 * keeps the widget fresh without hammering the backend.
 */
export function useAtRisk(filters: AtRiskFilters = {}) {
  return useQuery({
    queryKey: atRiskQueryKeys.list(filters),
    queryFn: () => getAtRiskStudents(filters),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

interface DismissArgs {
  studentId: string;
  /** Cohort scope for the dismissal (backend keys suppression on it). */
  cohortId?: string;
  note?: string;
}

/**
 * Dismiss a whole student from the at-risk list for 24h. The backend
 * suppression is per-student (not per-reason), so the optimistic update
 * removes the entire row — matching what the next poll will return. On error
 * we restore the previous snapshots and surface a toast.
 */
export function useDismissStudent() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ studentId, cohortId, note }: DismissArgs) =>
      dismissStudent(studentId, cohortId, note),
    onMutate: async ({ studentId }) => {
      await queryClient.cancelQueries({ queryKey: atRiskQueryKeys.all });

      // Snapshot every cached at-risk query so we can roll back on error.
      const snapshots = queryClient.getQueriesData<AtRiskStudent[]>({
        queryKey: atRiskQueryKeys.all,
      });

      snapshots.forEach(([key, data]) => {
        if (!data) return;
        // Per-student dismissal → drop the whole row.
        const next = data.filter((s) => s.studentId !== studentId);
        queryClient.setQueryData(key, next);
      });

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      // Roll back the optimistic update.
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      toast.showError('Couldn\'t dismiss student — please try again');
    },
    onSuccess: () => {
      toast.showSuccess('Student dismissed for 24h');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: atRiskQueryKeys.all });
    },
  });
}
